/* eslint-disable no-underscore-dangle */
import React, { useCallback, useMemo, useState } from 'react';
import { t } from 'ttag';

import { Drawer } from '@modules';
import { Heading, Text } from '@components';

export default function Contact() {
  const pageContext = useMemo(() => window?.__INITIAL_STATE__, []);

  const [isOpen, setIsOpen] = useState(false);

  const hasKindly = useMemo(() => !!window?.kindlyChat?.openChat, []);

  const openChat = useCallback(() => {
    if (hasKindly) {
      window.kindlyChat.openChat();
    }
  }, [hasKindly]);

  const phoneNumber = useMemo(() => window?.config?.phoneNumber, []);

  const supportEmail = useMemo(() => {
    const siteEmailAddress = window?.config?.siteEmailAddress;

    if (siteEmailAddress) {
      const decodedString = new DOMParser().parseFromString(
        siteEmailAddress,
        'text/html'
      ).body.textContent;

      const email = decodedString.match(
        /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/
      );
      return email[0];
    }

    return undefined;
  }, []);

  const isPhoneNumberEnabled = useMemo(
    () => window?.config?.isPhoneNumberEnabled,
    []
  );

  const isEmailEnabled = useMemo(() => window?.config?.isEmailEnabled, []);

  return (
    <div>
      <div className="space-y-4 rounded-lg bg-white p-4">
        <Heading as="h2" className="text-lg normal-case md:text-lg">
          {t`Contact Us`}
        </Heading>
        <Text className="block">{t`You are always welcome to contact us, if you need further assistance.`}</Text>
        <button
          type="button"
          className="m-0 p-0 bg-transparent border-0 font-bold text-black underline outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 focus-visible:ring-offset-white"
          onClick={() => setIsOpen(true)}
        >
          {t`Contact Information`}
        </button>
      </div>
      <Drawer
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        onClose={() => {}}
        className="p-8"
      >
        <Drawer.Close />
        <Drawer.Title>
          <Heading
            as="h2"
            className="text-xl font-semibold normal-case md:text-xl"
          >
            {t`Contact Us`}
          </Heading>
        </Drawer.Title>
        <Drawer.Content className="space-y-8">
          <Text className="block">
            {t`You are always welcome to reach out to us either by phone or email. We strive to respond to all email inquiries within 48 hours on business days. However, please note that response times may be slightly longer during peak periods.`}
            <br />
            <br />
            {t`Our Frequently Asked Questions (FAQ) section is designed to provide answers to common queries such as returns, complaints, and delivery.`}
          </Text>
          <section className="space-y-8">
            {/* Chat */}
            <div className="space-y-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                className="h-6 w-6"
              >
                <path d="M10 3H14C18.4183 3 22 6.58172 22 11C22 15.4183 18.4183 19 14 19V22.5C9 20.5 2 17.5 2 11C2 6.58172 5.58172 3 10 3Z" />
              </svg>
              <Heading
                as="h3"
                className="text-lg font-semibold normal-case md:text-lg"
              >
                {t`Chat`}
              </Heading>
              <Text className="text-sm">
                {t`Get instant answers — Unibot can answer most questions.`}
              </Text>
              <button
                type="button"
                className="m-0 p-0 bg-transparent border-0 font-bold text-black underline outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 focus-visible:ring-offset-white"
                onClick={() => {
                  setIsOpen(false);
                  openChat();
                }}
              >
                {t`Chat with Us`}
              </button>
            </div>

            {/* Email */}
            {supportEmail && isEmailEnabled && (
              <div className="space-y-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  className="h-6 w-6"
                >
                  <path d="M3 3H21C21.5523 3 22 3.44772 22 4V20C22 20.5523 21.5523 21 21 21H3C2.44772 21 2 20.5523 2 20V4C2 3.44772 2.44772 3 3 3ZM12.0606 11.6829L5.64722 6.2377L4.35278 7.7623L12.0731 14.3171L19.6544 7.75616L18.3456 6.24384L12.0606 11.6829Z" />
                </svg>
                <Heading
                  as="h3"
                  className="text-lg font-semibold normal-case md:text-lg"
                >
                  {t`Email`}
                </Heading>
                <Text className="text-sm">
                  {t`Email inquiries are typically answered within 48 hours on weekdays. However, it may take longer during busy periods.`}
                </Text>
                <a
                  href={`mailto:${supportEmail}`}
                  className="block text-sm font-bold text-black underline"
                >
                  {supportEmail}
                </a>
              </div>
            )}

            {/* Teamsport */}
            {isEmailEnabled && (
              <div className="space-y-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  className="h-6 w-6"
                >
                  <path d="M12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2ZM13.6695 15.9999H10.3295L8.95053 17.8969L9.5044 19.6031C10.2897 19.8607 11.1286 20 12 20C12.8714 20 13.7103 19.8607 14.4956 19.6031L15.0485 17.8969L13.6695 15.9999ZM5.29354 10.8719L4.00222 11.8095L4 12C4 13.7297 4.54894 15.3312 5.4821 16.6397L7.39254 16.6399L8.71453 14.8199L7.68654 11.6499L5.29354 10.8719ZM18.7055 10.8719L16.3125 11.6499L15.2845 14.8199L16.6065 16.6399L18.5179 16.6397C19.4511 15.3312 20 13.7297 20 12L19.997 11.809L18.7055 10.8719ZM14.2914 4.33299L12.9995 5.27293V7.78993L15.6935 9.74693L17.9325 9.01993L18.4867 7.3168C17.467 5.90685 15.9988 4.84254 14.2914 4.33299ZM9.70857 4.33299C8.00078 4.84265 6.53236 5.90735 5.51261 7.31778L6.06653 9.01993L8.30554 9.74693L10.9995 7.78993V5.27293L9.70857 4.33299Z" />
                </svg>
                <Heading
                  as="h3"
                  className="text-lg font-semibold normal-case md:text-lg"
                >
                  {t`Teamsport`}
                </Heading>
                <Text className="text-sm">
                  {t`If you have any questions regarding Unisport Teamsport, please contact Unisport's Teamsport department via email.`}
                </Text>
                <a
                  href={`mailto:${t`teamsport@unisportstore.com`}`}
                  className="block text-sm font-bold text-black underline"
                >
                  {t`teamsport@unisportstore.com`}
                </a>
              </div>
            )}

            {/* Phone */}
            {phoneNumber && isPhoneNumberEnabled && (
              <div className="space-y-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  className="h-6 w-6"
                >
                  <path d="M21 8C22.1046 8 23 8.89543 23 10V14C23 15.1046 22.1046 16 21 16H19.9381C19.446 19.9463 16.0796 23 12 23V21C15.3137 21 18 18.3137 18 15V9C18 5.68629 15.3137 3 12 3C8.68629 3 6 5.68629 6 9V16H3C1.89543 16 1 15.1046 1 14V10C1 8.89543 1.89543 8 3 8H4.06189C4.55399 4.05369 7.92038 1 12 1C16.0796 1 19.446 4.05369 19.9381 8H21ZM7.75944 15.7849L8.81958 14.0887C9.74161 14.6662 10.8318 15 12 15C13.1682 15 14.2584 14.6662 15.1804 14.0887L16.2406 15.7849C15.0112 16.5549 13.5576 17 12 17C10.4424 17 8.98882 16.5549 7.75944 15.7849Z" />
                </svg>
                <Heading
                  as="h3"
                  className="text-lg font-semibold normal-case md:text-lg"
                >
                  {t`Call Us`}
                </Heading>
                <Text className="text-sm">
                  {t`Our team is ready to answer all your questions.`}
                </Text>
                <Text className="text-sm font-semibold">
                  {t`Monday - Thursday:`}{' '}
                  {pageContext?.openingTime || t`09:00 - 15:00`}
                  <br />
                  {t`Friday:`}{' '}
                  {pageContext?.openingTimeFriday || t`09:00 - 14:00`}
                  <br />
                  {t`Weekend & Public Holidays: Closed`}
                </Text>
                <a
                  href={`tel:${phoneNumber.replace(/\s/g, '')}`}
                  className="block text-sm font-bold text-black underline"
                >
                  {phoneNumber}
                </a>
              </div>
            )}
          </section>
        </Drawer.Content>
      </Drawer>
    </div>
  );
}
